import { IntendedDisplay } from '@iheartjane/dm-sdk';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type {
  BannerCarouselSlide,
  CustomImageAdProduct,
} from '@iheartjane/menu';
import { BannerCarousel } from '@iheartjane/menu';
import { Box, useMobileMediaQuery } from '@iheartjane/reefer';
import { useCallback, useMemo } from 'react';

import { useCustomImageRow } from '@jane/dm/internal';
import { useSearchContext } from '@jane/search/hooks';
import { useMenu } from '@jane/shared-ecomm/providers';

import {
  buildCustomImageAdSlide,
  getMobileCarouselSlides,
  storeHasVisibleCarousel,
  toBannerCarouselSlides,
} from './MenuBannerCarousel.util';

const AD_SEARCH_ATTRIBUTES = [
  'aggregate_rating',
  'brand',
  'category',
  'image_urls',
  'name',
  'product_id',
  'review_count',
];

export const MenuBannerCarousel = () => {
  const { paths, store } = useMenu();
  const isMobile = useMobileMediaQuery({});
  const placeSecond = useFlag(FLAGS.p1PlaceImageAdSecond, false);
  const { searchState, setSearchState } = useSearchContext();

  const { instance, isLoading } = useCustomImageRow<CustomImageAdProduct>({
    searchAttributes: AD_SEARCH_ATTRIBUTES,
    storeId: Number(store.id),
  });

  const adSlide = useMemo(
    () =>
      buildCustomImageAdSlide({
        instance,
        intendedDisplay: isMobile
          ? IntendedDisplay.Mobile
          : IntendedDisplay.Desktop,
        onImageClick: (brandName) => {
          if (!brandName) return;

          setSearchState({
            filters: { ...searchState.filters, brand: [brandName] },
          });
        },
        toPdpUrl: ({ brand, product_id }) =>
          paths.menuProduct({ brand, id: product_id }),
      }),
    [instance, isMobile, paths, searchState, setSearchState]
  );

  const slides = useMemo<BannerCarouselSlide[]>(() => {
    const desktopCarouselSlides =
      store?.carousel_banner?.filter((i) => i.breakpoint === 'desktop') || [];

    const mobileCarouselSlides =
      store?.carousel_banner?.filter((i) => i.breakpoint === 'mobile') || [];

    const storeSlides = storeHasVisibleCarousel(store)
      ? toBannerCarouselSlides(
          isMobile
            ? getMobileCarouselSlides(
                mobileCarouselSlides,
                desktopCarouselSlides
              )
            : desktopCarouselSlides,
          store?.name
        )
      : [];

    if (adSlide == null) return storeSlides;
    if (storeSlides.length === 0) return [adSlide];
    if (!placeSecond) return [adSlide, ...storeSlides];

    return [storeSlides[0], adSlide, ...storeSlides.slice(1)];
  }, [adSlide, isMobile, placeSecond, store]);

  const handleView = useCallback(
    (slide: BannerCarouselSlide) => {
      if (slide?.kind !== 'customImageAd') return;

      instance?.products.forEach((product) => void product.impress());
    },
    [instance]
  );

  if (isLoading) return null;

  if (!slides.length) return null;

  return (
    <Box px={isMobile ? 24 : 64}>
      <BannerCarousel
        key={isMobile ? 'mobile' : 'desktop'}
        onView={handleView}
        slides={slides}
      />
    </Box>
  );
};
